import type { FourPillars } from '../types/pillars';
import type { ZodiacCompat } from '../types/compatibility';
import { branchIndex } from '../core/sexagenary';
import { SIX_COMBINES, THREE_COMBINES } from '../relations/earthly-combine';
import { CLASHES } from '../relations/earthly-clash';
import { TRIPLE_PUNISH_1, TRIPLE_PUNISH_2, MUTUAL_PUNISH } from '../relations/earthly-punish';

/**
 * 두 사주의 년지(띠) 궁합을 분석한다.
 */
export function analyzeZodiacCompat(fp1: FourPillars, fp2: FourPillars): ZodiacCompat {
  const b1 = fp1.year.branch;
  const b2 = fp2.year.branch;
  const i1 = branchIndex(b1);
  const i2 = branchIndex(b2);

  const relations: ZodiacCompat['relations'] = [];

  // 육합
  for (const [a, b] of SIX_COMBINES) {
    if ((a === i1 && b === i2) || (b === i1 && a === i2)) {
      relations.push({ type: '육합', description: `${b1}${b2} 육합` });
      break;
    }
  }

  // 삼합 (두 지지가 같은 삼합 조에 속하는지)
  for (const trio of THREE_COMBINES) {
    if (trio.includes(i1) && trio.includes(i2) && i1 !== i2) {
      relations.push({ type: '삼합', description: `${b1}${b2} 삼합 구성원` });
      break;
    }
  }

  // 육충
  for (const [a, b] of CLASHES) {
    if ((a === i1 && b === i2) || (b === i1 && a === i2)) {
      relations.push({ type: '육충', description: `${b1}${b2} 육충` });
      break;
    }
  }

  // 삼형
  for (const trio of [TRIPLE_PUNISH_1, TRIPLE_PUNISH_2]) {
    if (trio.includes(i1) && trio.includes(i2) && i1 !== i2) {
      relations.push({ type: '삼형', description: `${b1}${b2} 삼형 구성원` });
      break;
    }
  }

  // 자묘형 (子卯 상형)
  if ((MUTUAL_PUNISH[0] === i1 && MUTUAL_PUNISH[1] === i2) ||
      (MUTUAL_PUNISH[1] === i1 && MUTUAL_PUNISH[0] === i2)) {
    relations.push({ type: '자묘형', description: `${b1}${b2} 자묘형` });
  }

  let summary: string;
  if (relations.length === 0) {
    summary = `띠궁합: ${b1}띠 ↔ ${b2}띠 — 특별한 관계 없음`;
  } else {
    const names = relations.map(r => r.type).join(', ');
    summary = `띠궁합: ${b1}띠 ↔ ${b2}띠 — ${names}`;
  }

  return {
    person1Branch: b1,
    person2Branch: b2,
    relations,
    description: summary,
  };
}
